import { MAINNET_BRIDGE } from "config/default";
import { zeroAddress } from "ethereumjs-util";
import { DESTINATION_NETWORK } from "modules/bridge/config";
import { Address } from "viem";
import {
  useChainId,
  useSwitchChain,
  useTransactionReceipt,
  useWaitForTransactionReceipt,
  useWriteContract,
} from "wagmi";
import { Bridge__factory } from "../types/contracts/bridge";
import { Erc20__factory } from "../types/contracts/erc-20";

export type BridgeArgs = {
  amount: bigint;
  destinationAddress: Address;
  from: { chainId: number; bridgeContractAddress: Address };
  to: { networkId: number; bridgeContractAddress: Address };
  token: Address;
  tokenSpendPermission?: {
    type: string;
    permit?: any;
  };
  gas?: any;
  permitData?: string;
};

export const useBridge = ({
  amount,
  destinationAddress,
  from,
  to,
  token,
  gas,
}: BridgeArgs) => {
  const globalChainId = useChainId();
  const { switchChain } = useSwitchChain();

  const {
    writeContract: writeContractApprove,
    data: dataApprove,
    isPending: isApprovePending,
    reset: resetApprove,
  } = useWriteContract();

  const {
    isLoading: isApproveLoadingReceipt,
    isSuccess: isApproveSuccessReceipt,
  } = useTransactionReceipt({
    hash: dataApprove,
  });

  const { writeContract, data: hash, ...rest } = useWriteContract();

  const { isLoading: isConfirming, isSuccess: isConfirmed } =
    useWaitForTransactionReceipt({
      hash,
      query: { enabled: Boolean(hash), retry: true },
    });

  const isETH = token === zeroAddress();

  const approveToken = async () => {
    return writeContractApprove({
      address: token,
      abi: Erc20__factory.abi,
      functionName: "approve",
      args: [from.bridgeContractAddress, amount],
    });
  };

  const bridgeAsset = async () => {
    const overrides = {
      value: isETH ? amount : undefined,
      ...(gas ?? {}),
    };

    const args = [
      DESTINATION_NETWORK[to.networkId],
      destinationAddress,
      amount,
      token,
      true,
      "0x",
    ];

    if (globalChainId !== from.chainId) {
      switchChain?.({ chainId: from.chainId });
    }

    return writeContract({
      address: MAINNET_BRIDGE,
      abi: Bridge__factory.abi,
      functionName: "bridgeAsset",
      args,
      ...overrides,
    });
  };

  return {
    approveToken,
    bridgeAsset,
    hash,
    isConfirming,
    isConfirmed,
    isApprovePending,
    isApproveLoadingReceipt,
    isApproveSuccessReceipt,
    resetApprove,
    ...rest,
  };
};
